/** Sites manager — list parent + child sites, connect/disconnect Xero per site,
 * add new sites, and select which site's mappings table is currently visible. */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Plug, PlugsConnected, Storefront, Trash } from "@phosphor-icons/react";

export default function SitesPanel({ clientId, activeSiteId, onSelectSite }) {
  const [data, setData] = useState({ parent: null, sites: [] });
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [location, setLocation] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${clientId}/sites`);
    setData(data);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addSite = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    try {
      await api.post(`/clients/${clientId}/sites`, { label, location });
      toast.success(`Site '${label}' added`);
      setLabel(""); setLocation(""); setOpen(false);
      load();
    } catch {
      toast.error("Could not add site");
    }
  };

  const removeSite = async (siteId, name) => {
    if (!window.confirm(`Remove site '${name}' and all its data?`)) return;
    await api.delete(`/clients/${clientId}/sites/${siteId}`);
    toast.success("Site removed");
    if (activeSiteId === siteId) onSelectSite?.(null);
    load();
  };

  const connectXero = async (targetClientId) => {
    try {
      const { data } = await api.get(`/clients/${targetClientId}/xero/authorize`);
      try { window.top.location.href = data.authorize_url; }
      catch { window.open(data.authorize_url, "_blank", "noopener"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xero not configured");
    }
  };

  const disconnectXero = async (targetClientId) => {
    if (!window.confirm("Disconnect Xero from this site? Cached data will remain.")) return;
    await api.post(`/clients/${targetClientId}/xero/disconnect`);
    toast.success("Disconnected");
    load();
  };

  if (!data.parent) return null;
  const allSites = [data.parent, ...data.sites];

  return (
    <div className="bg-white border border-[#E7E5E4] rounded-md card-fade" data-testid="sites-panel">
      <div className="p-6 border-b border-[#E7E5E4] flex items-center justify-between gap-3">
        <div>
          <p className="uppercase text-[10px] tracking-[0.2em] text-[#57534E] font-medium mb-1">Locations · Xero connections</p>
          <h2 className="font-display text-2xl tracking-tight">Sites</h2>
          <p className="text-xs text-[#57534E] mt-1">One Xero connection per site. The Flash Report Location Hub will switch between these. <span className="text-[#1C1917] font-medium">Click any site row to edit its Chart of Accounts mappings.</span></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-site-btn" className="bg-[#1C1917] hover:bg-[#292524] text-white rounded-md h-10">
              <Plus size={14} className="mr-1.5" /> Add site
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">New site</DialogTitle>
            </DialogHeader>
            <form onSubmit={addSite} className="space-y-4 mt-2" data-testid="add-site-form">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#57534E]">Site label</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} required data-testid="site-label-input" className="rounded-md h-10" placeholder="e.g. Soho · Restaurant" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#57534E]">Location (optional)</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="site-location-input" className="rounded-md h-10" placeholder="London W1" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-site-btn" className="bg-[#1C1917] hover:bg-[#292524] text-white rounded-md">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="divide-y divide-[#F5F5F4]">
        {allSites.map((s, i) => {
          const isParent = i === 0;
          const isActive = (activeSiteId || data.parent.id) === s.id;
          return (
            <div
              key={s.id}
              data-testid={`site-row-${s.id}`}
              className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 px-6 py-4 items-center cursor-pointer transition-colors ${isActive ? "bg-[#FAFAF9]" : "hover:bg-[#FAF9F6]"}`}
              onClick={() => onSelectSite?.(s.id)}
            >
              <Storefront size={20} weight="duotone" className={isActive ? "text-[#1C1917]" : "text-[#A8A29E]"} />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="font-medium text-sm">{s.name}</p>
                  {isParent && <span className="text-[9px] uppercase tracking-wider text-[#A8A29E] bg-[#F5F5F4] px-1.5 py-0.5 rounded">Primary</span>}
                  {isActive && <span data-testid={`site-active-${s.id}`} className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Editing</span>}
                </div>
                <p className="text-xs text-[#57534E] font-mono mt-0.5">
                  {s.xero_tenant_name || s.location || "—"}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {s.xero_connected ? (
                  <>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-medium bg-emerald-50 text-emerald-700 flex items-center gap-1">
                      <PlugsConnected size={11} weight="duotone" /> Live Xero
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); disconnectXero(s.id); }}
                      data-testid={`disconnect-xero-${s.id}`}
                      className="rounded-md border-[#E7E5E4] text-xs h-8"
                    >Disconnect</Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); connectXero(s.id); }}
                    data-testid={`connect-xero-${s.id}`}
                    className="bg-[#13B5EA] hover:bg-[#0CA5D6] text-white rounded-md text-xs h-8"
                  >
                    <Plug size={12} className="mr-1" /> Connect Xero
                  </Button>
                )}
              </div>
              {!isParent && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeSite(s.id, s.name); }}
                  data-testid={`delete-site-${s.id}`}
                  className="text-[#A8A29E] hover:text-rose-700"
                  title="Remove site"
                >
                  <Trash size={14} />
                </button>
              )}
              {isParent && <span className="w-[14px]" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
